import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon,
  IonToast,
  useIonAlert
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import {
  moonOutline,
  sunnyOutline,
  textOutline,
  codeSlashOutline,
  trashOutline,
  diamondOutline,
  informationCircleOutline,
  documentTextOutline,
  shieldCheckmarkOutline,
  mailOutline,
  volumeHighOutline,
  volumeMuteOutline,
  phonePortraitOutline,
  downloadOutline,
  cloudUploadOutline,
  shareSocialOutline,
  starOutline,
  chevronForwardOutline,
  settingsOutline,
  checkmarkCircle
} from 'ionicons/icons';
import { useConfig } from '../hooks/useConfig';
import { storageService } from '../services/StorageService';
import { themeService } from '../services/ThemeService';
import { soundService } from '../services/SoundService';
import { hapticService } from '../services/HapticService';
import { notifySettingsChange } from '../hooks/useSettings';
import UpgradePrompt from '../components/UpgradePrompt';
import SEO from '../components/SEO';
import { seoService } from '../services/SEOService';
import './Settings.css';

/**
 * Settings Page - Beautiful, functional app settings
 */
function Settings() {
  const { config } = useConfig();
  const baseMeta = seoService.getPageMeta('home');
  const [presentAlert] = useIonAlert();
  const history = useHistory();
  const [darkMode, setDarkMode] = useState(themeService.isDarkMode());
  const [settings, setSettings] = useState(storageService.getSettings());
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showExportToast, setShowExportToast] = useState(false);
  const [showImportToast, setShowImportToast] = useState(false);

  useEffect(() => {
    // Load current settings on mount
    const savedSettings = storageService.getSettings();
    setSettings(savedSettings);
    // Read dark mode from saved settings, not from DOM body class
    // This ensures consistency on page refresh
    setDarkMode(savedSettings.darkMode || false);
  }, []);

  const handleDarkModeToggle = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    themeService.applyTheme(newDarkMode ? 'dark' : 'light');
    storageService.setDarkMode(newDarkMode);
  };

  const handleSoundToggle = () => {
    const newEnabled = settings.soundEnabled === false ? true : false;
    const newSettings = storageService.updateSetting('soundEnabled', newEnabled);
    setSettings(newSettings);
    // Play a click sound if enabling sounds
    if (newEnabled) {
      soundService.playClick();
    }
    hapticService.light();
  };

  const handleHapticToggle = () => {
    const newEnabled = settings.hapticEnabled === false ? true : false;
    const newSettings = storageService.updateSetting('hapticEnabled', newEnabled);
    setSettings(newSettings);
    soundService.playClick();
    // Provide haptic feedback if enabling
    if (newEnabled) {
      hapticService.medium();
    }
  };

  const handleFontSizeChange = (size) => {
    const newSettings = storageService.updateSetting('fontSize', size);
    setSettings(newSettings);
    // Apply font size to code editor CSS variable
    document.documentElement.style.setProperty('--code-font-size', `${size}px`);
    // Notify other components that settings changed
    notifySettingsChange();
    soundService.playClick();
    hapticService.light();
  };

  const handleLineNumbersToggle = () => {
    const newSettings = storageService.updateSetting('showLineNumbers', !settings.showLineNumbers);
    setSettings(newSettings);
    // Notify other components that settings changed
    notifySettingsChange();
    soundService.playClick();
    hapticService.light();
  };

  const handleResetProgress = () => {
    console.log('Resetting progress...');
    const success = storageService.clearAll();
    console.log('Clear all result:', success);
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleResetClick = () => {
    presentAlert({
      header: 'Reset All Progress?',
      message: 'This will permanently delete all your progress, achievements, streaks, and settings. This action cannot be undone!',
      cssClass: 'reset-alert',
      buttons: [
        { 
          text: 'Cancel', 
          role: 'cancel',
          cssClass: 'alert-cancel-btn'
        },
        { 
          text: 'Reset Everything', 
          role: 'destructive', 
          handler: handleResetProgress,
          cssClass: 'alert-danger-btn'
        }
      ]
    });
  };

  const handleExportData = () => {
    const data = storageService.exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${config?.app?.name || 'coding-app'}-backup.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportToast(true);
  };

  const handleImportData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const success = storageService.importData(event.target.result);
          if (success) {
            setShowImportToast(true);
            soundService.playSuccess();
            hapticService.success();
            // Increase delay so toast is visible before reload
            setTimeout(() => window.location.reload(), 2500);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleShareApp = () => {
    const appId = config?.app?.id;
    const appName = config?.app?.name || 'Learn to Code';
    const website = config?.app?.website;
    
    // Detect if running as native app (Capacitor) or web
    const isNativeApp = window.Capacitor?.isNativePlatform?.() || false;
    
    // Use Play Store URL for app, website URL for web
    const shareUrl = isNativeApp && appId
      ? `https://play.google.com/store/apps/details?id=${appId}`
      : website || window.location.origin;
    
    if (navigator.share) {
      navigator.share({
        title: appName,
        text: `Check out ${appName}! Learn coding the fun way! 🚀`,
        url: shareUrl
      });
    }
  };

  const handleRateApp = () => {
    const appId = config?.app?.id;
    if (appId) {
      window.open(`https://play.google.com/store/apps/details?id=${appId}`, '_system');
    }
  };

  const isFreVersion = config?.monetization?.enabled;
  const fontSizes = [
    { value: 12, label: 'Small' },
    { value: 14, label: 'Medium' },
    { value: 16, label: 'Large' },
    { value: 18, label: 'X-Large' }
  ];

  return (
    <>
      <SEO
        title="Settings - Customize Your Experience"
        description="Customize your learning experience with theme, sound, and notification settings."
        ogImage={baseMeta.ogImage}
      />
      <IonPage className="settings-page">
      <IonHeader className="ion-no-border">
        <IonToolbar className="settings-toolbar">
          <IonButtons slot="start">
            <IonBackButton defaultHref="/home" color="light" />
          </IonButtons>
          <IonTitle>Settings</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="settings-content">
        {/* Header Banner */}
        <div className="settings-header">
          <div className="settings-header-icon">
            <IonIcon icon={settingsOutline} />
          </div>
          <h1>Settings</h1>
          <p>Customize your learning experience</p>
        </div>

        <div className="settings-body">
          {/* Appearance Section */}
          <div className="settings-section">
            <div className="settings-section-title">
              <span>🎨</span> Appearance
            </div>
            <div className="settings-card">
              {/* Dark Mode */}
              <div className="settings-item" onClick={handleDarkModeToggle}>
                <div className="settings-item-left">
                  <div className={`settings-icon-wrapper ${darkMode ? 'dark' : 'light'}`}>
                    <IonIcon icon={darkMode ? moonOutline : sunnyOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Dark Mode</span>
                    <span className="settings-item-subtitle">
                      {darkMode ? 'Dark theme active' : 'Light theme active'}
                    </span>
                  </div>
                </div>
                <div className={`custom-toggle ${darkMode ? 'active' : ''}`}>
                  <div className="toggle-track">
                    <div className="toggle-thumb">
                      <IonIcon icon={darkMode ? moonOutline : sunnyOutline} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Code Editor Section */}
          <div className="settings-section">
            <div className="settings-section-title">
              <span>💻</span> Code Editor
            </div>
            <div className="settings-card">
              {/* Font Size */}
              <div className="settings-item font-size-item">
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper code">
                    <IonIcon icon={textOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Font Size</span>
                    <span className="settings-item-subtitle">Code editor text size</span>
                  </div>
                </div>
              </div>
              <div className="font-size-options">
                {fontSizes.map(size => (
                  <button
                    key={size.value}
                    className={`font-size-btn ${(settings.fontSize || 14) === size.value ? 'active' : ''}`}
                    onClick={() => handleFontSizeChange(size.value)}
                  >
                    <span className="size-label">{size.label}</span>
                    <span className="size-value">{size.value}px</span>
                    {(settings.fontSize || 14) === size.value && (
                      <IonIcon icon={checkmarkCircle} className="check-icon" />
                    )}
                  </button>
                ))}
              </div>

              {/* Line Numbers */}
              <div className="settings-item" onClick={handleLineNumbersToggle}>
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper code">
                    <IonIcon icon={codeSlashOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Line Numbers</span>
                    <span className="settings-item-subtitle">Show line numbers in editor</span>
                  </div>
                </div>
                <div className={`custom-toggle ${settings.showLineNumbers !== false ? 'active' : ''}`}>
                  <div className="toggle-track">
                    <div className="toggle-thumb" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sound & Haptics Section */}
          <div className="settings-section">
            <div className="settings-section-title">
              <span>🔊</span> Sound & Haptics
            </div>
            <div className="settings-card">
              {/* Sound Effects */}
              <div className="settings-item" onClick={handleSoundToggle}>
                <div className="settings-item-left">
                  <div className={`settings-icon-wrapper ${settings.soundEnabled ? 'success' : 'muted'}`}>
                    <IonIcon icon={settings.soundEnabled ? volumeHighOutline : volumeMuteOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Sound Effects</span>
                    <span className="settings-item-subtitle">
                      {settings.soundEnabled !== false ? 'Sounds enabled' : 'Sounds muted'}
                    </span>
                  </div>
                </div>
                <div className={`custom-toggle ${settings.soundEnabled !== false ? 'active' : ''}`}>
                  <div className="toggle-track">
                    <div className="toggle-thumb" />
                  </div>
                </div>
              </div>

              {/* Haptic Feedback */}
              <div className="settings-item" onClick={handleHapticToggle}>
                <div className="settings-item-left">
                  <div className={`settings-icon-wrapper ${settings.hapticEnabled ? 'success' : 'muted'}`}>
                    <IonIcon icon={phonePortraitOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Haptic Feedback</span>
                    <span className="settings-item-subtitle">
                      {settings.hapticEnabled !== false ? 'Vibration enabled' : 'Vibration disabled'}
                    </span>
                  </div>
                </div>
                <div className={`custom-toggle ${settings.hapticEnabled !== false ? 'active' : ''}`}>
                  <div className="toggle-track">
                    <div className="toggle-thumb" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Management Section */}
          <div className="settings-section">
            <div className="settings-section-title">
              <span>💾</span> Data Management
            </div>
            <div className="settings-card">
              {/* Export Data */}
              <div className="settings-item" onClick={handleExportData}>
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper export">
                    <IonIcon icon={downloadOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Export Data</span>
                    <span className="settings-item-subtitle">Download your progress backup</span>
                  </div>
                </div>
                <IonIcon icon={chevronForwardOutline} className="chevron-icon" />
              </div>

              {/* Import Data */}
              <div className="settings-item" onClick={handleImportData}>
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper import">
                    <IonIcon icon={cloudUploadOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Import Data</span>
                    <span className="settings-item-subtitle">Restore from backup file</span>
                  </div>
                </div>
                <IonIcon icon={chevronForwardOutline} className="chevron-icon" />
              </div>

              {/* Reset Progress */}
              <div className="settings-item danger" onClick={handleResetClick}>
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper danger">
                    <IonIcon icon={trashOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title danger">Reset All Progress</span>
                    <span className="settings-item-subtitle">Delete all data and start fresh</span>
                  </div>
                </div>
                <IonIcon icon={chevronForwardOutline} className="chevron-icon" />
              </div>
            </div>
          </div>

          {/* Upgrade Section (FREE version only) */}
          {isFreVersion && (
            <div className="settings-section">
              <div className="settings-section-title">
                <span>👑</span> Premium
              </div>
              <div className="upgrade-card" onClick={() => setShowUpgrade(true)}>
                <div className="upgrade-gradient" />
                <div className="upgrade-content">
                  <div className="upgrade-icon">
                    <IonIcon icon={diamondOutline} />
                  </div>
                  <div className="upgrade-text">
                    <h3>Upgrade to Pro</h3>
                    <p>Remove ads & unlock premium features</p>
                  </div>
                  <div className="upgrade-price">
                    <span className="price">{config?.monetization?.upgradePrice || '$4.99'}</span>
                    <span className="label">One-time</span>
                  </div>
                </div>
                <div className="upgrade-features">
                  <div className="feature">✨ Ad-free experience</div>
                  <div className="feature">🎯 Exclusive content</div>
                  <div className="feature">⚡ Priority support</div>
                </div>
                <IonButton 
                  expand="block" 
                  className="upgrade-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    const upgradeUrl = config?.monetization?.upgradeUrl;
                    if (upgradeUrl) {
                      window.open(upgradeUrl, '_system');
                    }
                  }}
                >
                  Upgrade Now
                </IonButton>
              </div>
            </div>
          )}

          {/* Support Section */}
          <div className="settings-section">
            <div className="settings-section-title">
              <span>💬</span> Support
            </div>
            <div className="settings-card">
              {/* Rate App */}
              <div className="settings-item" onClick={handleRateApp}>
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper star">
                    <IonIcon icon={starOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Rate App</span>
                    <span className="settings-item-subtitle">Love the app? Leave a review!</span>
                  </div>
                </div>
                <IonIcon icon={chevronForwardOutline} className="chevron-icon" />
              </div>

              {/* Share App */}
              <div className="settings-item" onClick={handleShareApp}>
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper share">
                    <IonIcon icon={shareSocialOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Share App</span>
                    <span className="settings-item-subtitle">Tell your friends about us</span>
                  </div>
                </div>
                <IonIcon icon={chevronForwardOutline} className="chevron-icon" />
              </div>

              {/* Contact Support */}
              <div className="settings-item" onClick={() => window.open(`mailto:${config?.app?.supportEmail}`, '_blank')}>
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper mail">
                    <IonIcon icon={mailOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Contact Support</span>
                    <span className="settings-item-subtitle">{config?.app?.supportEmail || 'Get help'}</span>
                  </div>
                </div>
                <IonIcon icon={chevronForwardOutline} className="chevron-icon" />
              </div>
            </div>
          </div>

          {/* About Section */}
          <div className="settings-section">
            <div className="settings-section-title">
              <span>ℹ️</span> About
            </div>
            <div className="settings-card">
              {/* About App */}
              <div className="settings-item" onClick={() => history.push('/about')}>
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper info">
                    <IonIcon icon={informationCircleOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">About App</span>
                    <span className="settings-item-subtitle">Learn more about {config?.app?.name || 'this app'}</span>
                  </div>
                </div>
                <IonIcon icon={chevronForwardOutline} className="chevron-icon" />
              </div>

              {/* Version */}
              <div className="settings-item">
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper version">
                    <IonIcon icon={codeSlashOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Version</span>
                    <span className="settings-item-subtitle">{config?.app?.version || '1.0.0'}</span>
                  </div>
                </div>
                <span className="version-badge">
                  {config?.app?.versionType === 'paid' ? '👑 Pro' : '🆓 Free'}
                </span>
              </div>

              {/* Privacy Policy */}
              <div className="settings-item" onClick={() => history.push('/privacy-policy')}>
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper privacy">
                    <IonIcon icon={shieldCheckmarkOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Privacy Policy</span>
                    <span className="settings-item-subtitle">How we protect your data</span>
                  </div>
                </div>
                <IonIcon icon={chevronForwardOutline} className="chevron-icon" />
              </div>

              {/* Terms of Service */}
              <div className="settings-item" onClick={() => history.push('/terms-of-service')}>
                <div className="settings-item-left">
                  <div className="settings-icon-wrapper terms">
                    <IonIcon icon={documentTextOutline} />
                  </div>
                  <div className="settings-item-text">
                    <span className="settings-item-title">Terms of Service</span>
                    <span className="settings-item-subtitle">App usage terms</span>
                  </div>
                </div>
                <IonIcon icon={chevronForwardOutline} className="chevron-icon" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="settings-footer">
            <p>Made with ❤️ for learners everywhere</p>
            <p className="copyright">© {new Date().getFullYear()} {config?.app?.author || 'Your Name'}</p>
          </div>
        </div>

        {/* Upgrade Prompt */}
        <UpgradePrompt 
          isOpen={showUpgrade} 
          onClose={() => setShowUpgrade(false)} 
        />

        {/* Export Toast */}
        <IonToast
          isOpen={showExportToast}
          onDidDismiss={() => setShowExportToast(false)}
          message="Data exported successfully! 📥"
          duration={2000}
          position="bottom"
          color="success"
        />

        {/* Import Toast */}
        <IonToast
          isOpen={showImportToast}
          onDidDismiss={() => setShowImportToast(false)}
          message="Data imported! Reloading... ✅"
          duration={2500}
          position="bottom"
          color="success"
        />
      </IonContent>
    </IonPage>
    </>
  );
}

export default Settings;